import { useEffect, useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Loader2, Save, Wrench, ShieldCheck, Percent } from "lucide-react";
import { api } from "@/lib/api";

export function OrderWorkInfo({ order, onSave, saving }) {
  const [mechanics, setMechanics] = useState([]);
  const [consultants, setConsultants] = useState([]);
  useEffect(() => { api.get("/erp/mechanics").then(({ data }) => setMechanics(data)).catch(() => setMechanics([])); api.get("/consultants").then(({ data }) => setConsultants(data.items.map((c) => c.name))).catch(() => {}); }, []);
  const consOptions = [...new Set([...consultants, ...(order?.consultant_name ? [order.consultant_name] : [])])];
  const mechOptions = [...new Set([...mechanics.map((m) => m.name), ...(order?.mechanic_name ? [order.mechanic_name] : [])])];
  const [draft, setDraft] = useState({
    mechanic_name: "",
    consultant_name: "",
    warranty_days: "",
    warranty_km: "",
    commission_type: "none",
    commission_value: "",
  });

  useEffect(() => {
    if (!order) return;
    setDraft({
      mechanic_name: order.mechanic_name || "",
      consultant_name: order.consultant_name || "",
      warranty_days: order.warranty_days ?? "",
      warranty_km: order.warranty_km ?? "",
      commission_type: order.commission_type || "none",
      commission_value: order.commission_value ?? "",
    });
  }, [order?.id, order?.mechanic_name, order?.consultant_name, order?.warranty_days, order?.warranty_km, order?.commission_type, order?.commission_value]);

  const submit = () => {
    const payload = {
      mechanic_name: draft.mechanic_name || null,
      consultant_name: draft.consultant_name || null,
      warranty_days: draft.warranty_days === "" ? null : Number(draft.warranty_days),
      warranty_km: draft.warranty_km === "" ? null : Number(draft.warranty_km),
      commission_type: draft.commission_type === "none" ? null : draft.commission_type,
      commission_value: draft.commission_value === "" || draft.commission_type === "none" ? null : Number(draft.commission_value),
    };
    onSave(payload);
  };

  const changed = order && (
    (order.mechanic_name || "") !== (draft.mechanic_name || "") ||
    (order.consultant_name || "") !== (draft.consultant_name || "") ||
    String(order.warranty_days ?? "") !== String(draft.warranty_days ?? "") ||
    String(order.warranty_km ?? "") !== String(draft.warranty_km ?? "") ||
    (order.commission_type || "none") !== draft.commission_type ||
    String(order.commission_value ?? "") !== String(draft.commission_value ?? "")
  );

  return (
    <div data-testid="order-work-info" className="rounded-2xl border border-card2-border bg-appbg-soft/40 p-4 space-y-3">
      <div className="flex items-center gap-2 text-xs font-mono-caps text-zinc-500 uppercase tracking-widest">
        <Wrench className="h-4 w-4" /> Mecânico, consultor, garantia e comissão
      </div>
      <div>
        <Label className="text-xs font-mono-caps text-zinc-400">Mecânico responsável</Label>
        <Select value={draft.mechanic_name || "__none__"} onValueChange={(v) => setDraft((p) => ({ ...p, mechanic_name: v === "__none__" ? "" : v }))}>
          <SelectTrigger data-testid="order-mechanic-name" className="mt-1 bg-zinc-900 border-zinc-800"><SelectValue placeholder="Selecione" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Nenhum</SelectItem>
            {mechOptions.map((n) => <SelectItem key={n} value={n} data-testid={`mechanic-option-${n}`}>{n}{!mechanics.some((m) => m.name === n) ? " (antigo)" : ""}</SelectItem>)}
          </SelectContent>
        </Select>
        {mechanics.length === 0 && <p className="text-[11px] text-zinc-500 mt-1">Cadastre os mecânicos em Configurações.</p>}
      </div>
      <div>
        <Label className="text-xs font-mono-caps text-zinc-400">Consultor responsável</Label>
        <Select value={draft.consultant_name || "__none__"} onValueChange={(v) => setDraft((p) => ({ ...p, consultant_name: v === "__none__" ? "" : v }))}>
          <SelectTrigger data-testid="order-consultant-name" className="mt-1 bg-zinc-900 border-zinc-800"><SelectValue placeholder="Selecione" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Nenhum</SelectItem>
            {consOptions.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-mono-caps text-zinc-400 flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Garantia (dias)</Label>
          <Input data-testid="order-warranty-days" type="number" min={0} value={draft.warranty_days}
            onChange={(e) => setDraft((p) => ({ ...p, warranty_days: e.target.value }))}
            className="mt-1 bg-zinc-900 border-zinc-800" placeholder="90" />
        </div>
        <div>
          <Label className="text-xs font-mono-caps text-zinc-400">Garantia (km)</Label>
          <Input data-testid="order-warranty-km" type="number" min={0} value={draft.warranty_km}
            onChange={(e) => setDraft((p) => ({ ...p, warranty_km: e.target.value }))}
            className="mt-1 bg-zinc-900 border-zinc-800" placeholder="5000" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-mono-caps text-zinc-400 flex items-center gap-1"><Percent className="h-3 w-3" /> Comissão</Label>
          <Select value={draft.commission_type} onValueChange={(v) => setDraft((p) => ({ ...p, commission_type: v }))}>
            <SelectTrigger data-testid="order-commission-type" className="mt-1 bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800">
              <SelectItem value="none">Sem comissão</SelectItem>
              <SelectItem value="percent">% do total</SelectItem>
              <SelectItem value="fixed">Valor fixo</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-mono-caps text-zinc-400">{draft.commission_type === "fixed" ? "Valor (R$)" : "% ou valor"}</Label>
          <Input data-testid="order-commission-value" inputMode="decimal" disabled={draft.commission_type === "none"}
            value={draft.commission_value}
            onChange={(e) => setDraft((p) => ({ ...p, commission_value: e.target.value.replace(",", ".") }))}
            className="mt-1 bg-zinc-900 border-zinc-800 disabled:opacity-50"
            placeholder={draft.commission_type === "percent" ? "10" : "150,00"} />
        </div>
      </div>
      <div className="flex justify-end">
        <Button data-testid="order-work-save" type="button" onClick={submit} disabled={saving || !changed}
          className="h-9 bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Salvar</>}
        </Button>
      </div>
    </div>
  );
}
